import { createRoute, lazyRouteComponent, redirect } from '@tanstack/react-router'
import { z } from 'zod'

import { authenticatedRoute } from '@/app/routes/authenticated'
import { pageSearch as page, textSearch as text } from '@/shared/api/search'
import { requirePermission } from '@/shared/auth/guards'

const listSearch = z.object({ page, search: text, status: text })
const pushSearch = z.object({ page, search: text, status: text, source: text })
const templatesSearch = z.object({ page, search: text, type: text })
const recipientsSearch = z.object({ page, search: text, status: text })

export const notificationsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/notifications',
  beforeLoad: requirePermission('notifications.view'),
})

export const notificationsIndexRoute = createRoute({
  getParentRoute: () => notificationsRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/notifications/push' })
  },
})

export const pushListRoute = createRoute({
  getParentRoute: () => notificationsRoute,
  path: 'push',
  validateSearch: (search) => pushSearch.parse(search),
  component: lazyRouteComponent(() => import('./pages/push-page'), 'PushPage'),
})

// "new" tem prioridade sobre "$pushId".
export const pushCreateRoute = createRoute({
  getParentRoute: () => notificationsRoute,
  path: 'push/new',
  beforeLoad: requirePermission('notifications.manage'),
  component: lazyRouteComponent(() => import('./pages/push-form-page'), 'PushFormPage'),
})

export const pushDetailRoute = createRoute({
  getParentRoute: () => notificationsRoute,
  path: 'push/$pushId',
  component: lazyRouteComponent(() => import('./pages/push-detail-page'), 'PushDetailPage'),
})

export const campaignsRoute = createRoute({
  getParentRoute: () => notificationsRoute,
  path: 'campaigns',
  validateSearch: (search) => listSearch.parse(search),
  component: lazyRouteComponent(() => import('./pages/campaigns-page'), 'CampaignsPage'),
})

export const campaignCreateRoute = createRoute({
  getParentRoute: () => notificationsRoute,
  path: 'campaigns/new',
  beforeLoad: requirePermission('notifications.manage'),
  component: lazyRouteComponent(() => import('./pages/campaign-form-page'), 'CampaignFormPage'),
})

export const campaignEditRoute = createRoute({
  getParentRoute: () => notificationsRoute,
  path: 'campaigns/$campaignId/edit',
  beforeLoad: requirePermission('notifications.manage'),
  component: lazyRouteComponent(() => import('./pages/campaign-form-page'), 'CampaignFormPage'),
})

export const campaignDetailRoute = createRoute({
  getParentRoute: () => notificationsRoute,
  path: 'campaigns/$campaignId',
  validateSearch: (search) => recipientsSearch.parse(search),
  component: lazyRouteComponent(() => import('./pages/campaign-detail-page'), 'CampaignDetailPage'),
})

export const templatesRoute = createRoute({
  getParentRoute: () => notificationsRoute,
  path: 'templates',
  validateSearch: (search) => templatesSearch.parse(search),
  component: lazyRouteComponent(() => import('./pages/templates-page'), 'TemplatesPage'),
})

export const notificationSettingsRoute = createRoute({
  getParentRoute: () => notificationsRoute,
  path: 'settings',
  beforeLoad: requirePermission('notifications.manage'),
  validateSearch: (search) =>
    z.object({ tab: z.enum(['push', 'mail']).optional().catch(undefined) }).parse(search),
  component: lazyRouteComponent(() => import('./pages/settings-page'), 'NotificationSettingsPage'),
})

export const notificationsRoutes = notificationsRoute.addChildren([
  notificationsIndexRoute,
  pushListRoute,
  pushCreateRoute,
  pushDetailRoute,
  campaignsRoute,
  campaignCreateRoute,
  campaignEditRoute,
  campaignDetailRoute,
  templatesRoute,
  notificationSettingsRoute,
])
